import type { Selector } from '@reduxjs/toolkit';
import { createSelector } from '@reduxjs/toolkit';
import qs from 'querystring';
import * as R from 'ramda';

import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { selectPathname } from 'src/redux/navigation';
import { selectRequestOptionsAsQueryObject } from 'src/redux/products/selectors';
import type { SelectedFilterOption, TreeFilterOption } from 'src/types';
import type { FilterOptionsProps } from '../../containers/FilterOptionsContainer.types';
import { selectSelectedFilterOptions } from './selectors';

export const ALL_CATEGORIES_DEPTH_VALUE = -1;

const selectFilterUidProp = (_: ReduxApplicationState, { filter }: FilterOptionsProps) =>
  filter.uid;

const selectFilterOptions = (_: ReduxApplicationState, { filter }: FilterOptionsProps) =>
  filter.options;

export const selectRouterPathWithoutCurrentFilter = createSelector(
  selectPathname,
  selectRequestOptionsAsQueryObject,
  selectFilterUidProp,
  (pathname, query, filterUid) => {
    const queryString = qs.stringify({
      ...R.omit([filterUid], query),
    });
    return queryString.length > 0 ? `${pathname}?${queryString}` : pathname;
  },
);

const selectActiveNodeIndex = (_: ReduxApplicationState, { filter }: FilterOptionsProps) =>
  filter.options.findIndex(({ active }) => active);

export const selectParentNodes: Selector<ReduxApplicationState, TreeFilterOption[]> =
  createSelector(selectActiveNodeIndex, selectFilterOptions, (activeNodeIndex, filterOptions) => {
    const noChildNodes = activeNodeIndex === -1;
    const parentNodes = noChildNodes ? filterOptions : filterOptions.slice(0, activeNodeIndex + 1);
    return parentNodes.map((node, index) => {
      const depthValue = index === 0 ? ALL_CATEGORIES_DEPTH_VALUE : index;
      const depth = noChildNodes ? 1 : depthValue;
      return { ...node, depth };
    });
  });

export const selectChildNodes = createSelector(
  selectActiveNodeIndex,
  selectFilterOptions,
  (activeNodeIndex, filterOptions): TreeFilterOption[] => {
    const childNodes = activeNodeIndex === -1 ? [] : filterOptions.slice(activeNodeIndex + 1);
    const depthValueOfParent = activeNodeIndex;
    return childNodes.map((node) => {
      const depth = depthValueOfParent + 1;
      return { ...node, depth };
    });
  },
);

export const selectDepthOfSelectedFilterOption = createSelector(
  (_: ReduxApplicationState, { filterUid }: SelectedFilterOption) => filterUid,
  selectSelectedFilterOptions,
  (filterUid, selectedFilterOptions) =>
    selectedFilterOptions.find((filter) => filter.filterUid === filterUid)?.filterOptionDepth,
);
